/**
 * 回复输入区组件（极简版）
 *
 * 文本输入框 + 清空/发送按钮
 * 48h 窗口过期时禁用
 */

import { useState, useCallback, useRef } from 'react';
import { Button, TextArea, TextField } from '@heroui/react';
import { IconSend as Send, IconEraser as Eraser } from '@tabler/icons-react';

interface ReplyInputProps {
  onSend: (content: string) => Promise<void>;
  disabled?: boolean;
  sending?: boolean;
  expiredMessage?: string;
}

export function ReplyInput({ onSend, disabled, sending, expiredMessage }: ReplyInputProps) {
  const [content, setContent] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const focusTextarea = useCallback(() => {
    const el = rootRef.current?.querySelector('textarea');
    (el as HTMLTextAreaElement | undefined)?.focus();
  }, []);

  const handleSend = useCallback(async () => {
    const trimmed = content.trim();
    if (!trimmed || disabled || sending) return;
    // 让错误冒泡到全局 toaster 处理
    await onSend(trimmed);
    setContent('');
    focusTextarea();
  }, [content, disabled, sending, onSend, focusTextarea]);

  const handleClear = useCallback(() => {
    setContent('');
    focusTextarea();
  }, [focusTextarea]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Ctrl+Enter 或 Cmd+Enter 发送
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  if (expiredMessage) {
    return (
      <div className="shrink-0 border-t border-divider bg-muted/30 px-4 py-3">
        <p className="text-center text-sm text-muted-foreground">{expiredMessage}</p>
      </div>
    );
  }

  const busy = Boolean(disabled || sending);
  const canClear = Boolean(content) && !sending;
  const canSend = Boolean(content.trim()) && !busy;

  return (
    <div ref={rootRef} className="shrink-0 border-t border-divider px-4 py-3">
      <div className="flex gap-2">
        <TextField className="min-w-0 flex-1" isDisabled={busy}>
          <TextArea
            aria-label="回复内容"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="请输入回复内容（Ctrl+Enter 发送）"
            className="min-h-[60px] max-h-[120px] resize-none text-sm"
            rows={2}
          />
        </TextField>
        <div className="flex shrink-0 flex-col gap-1.5">
          <Button
            variant="secondary"
            size="sm"
            onPress={handleClear}
            isDisabled={!canClear}
            aria-label="清空"
          >
            <span className="inline-flex items-center gap-1.5">
              <Eraser className="size-4 shrink-0" stroke={1.5} />
              <span>清空</span>
            </span>
          </Button>
          <Button
            variant="primary"
            size="sm"
            onPress={() => void handleSend()}
            isDisabled={!canSend}
            aria-label="发送"
          >
            <span className="inline-flex items-center gap-1.5">
              <Send className="size-4 shrink-0" stroke={1.5} />
              <span>{sending ? '发送中...' : '发送'}</span>
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
}
